// SearchBar.tsx
import React, { useState, useCallback } from "react";
import { TextInput, Button, Group } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { Filter } from "lucide-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

interface SearchBarProps {
  activeDrawer?: "notifications" | "notifications-detail" | "filter" | null;
  setActiveDrawer: (drawer: SearchBarProps["activeDrawer"]) => void;
  onSearch?: (value: string) => void;
  defaultValue?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  activeDrawer,
  setActiveDrawer,
  onSearch,
  defaultValue = "",
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState(defaultValue);
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (onSearch) {
        onSearch(value);
      }
    },
    [onSearch]
  );

  return (
    <Group
      align="center"
      spacing="sm"
      sx={{
        padding: "16px 0",
        borderBottom: "1px dashed #D8DDE2",
      }}
    >
      <TextInput
        value={searchValue}
        onChange={(event) => handleSearch(event.currentTarget.value)}
        placeholder={t<string>("SEARCH") || ""}
        size="md"
        sx={(theme) => ({
          flex: 1,
          "& input": {
            height: "44px",
            fontSize: "15px",
            "&::placeholder": {
              color: theme.colors.gray[6],
            },
            "&:focus": {
              borderColor: "#2A81D0",
            },
          },
          "& .mantine-TextInput-wrapper": {
            height: "44px",
          },
          "& .mantine-TextInput-input": {
            borderColor: "#D8DDE2",
            borderRadius: "10px",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#2A81D0",
            },
          },
        })}
        icon={<IconSearch size={20} style={{ color: "#94A3B8" }} />}
      />
      <Button
        variant="subtle"
        sx={(theme) => ({
          backgroundColor: "#EAF4FF",
          color: "#2A81D0",
          width: "44px",
          height: "44px",
          borderRadius: "10px",
          padding: 0,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "#DDE9F7",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        })}
        onClick={() => setActiveDrawer("filter")}
      >
        <Filter size={24} strokeWidth={1.5} />
      </Button>
    </Group>
  );
};

export default SearchBar;
